export type LayoutElement = { id?: string; sys_column_id: string; column_index: "1" | "2"; order: number };
export type EditorSection = {
    id: string; label: string; layout_columns: "1" | "2"; render_mode: "inline" | "tab"; order: number; active: boolean; elements: LayoutElement[];
    /** Texto já resolvido no idioma atual (o `label` guarda a chave de tradução ou o texto digitado). */
    display?: string;
    /** `false` esconde o título visual da seção; o título continua obrigatório e vira `aria-label`. */
    show_label?: boolean;
};

type ApiSection = { id: string; label: string; label_key?: string; layout_columns: string | number; render_mode?: string; order: number; active: boolean; show_label?: boolean | null };
type ApiElement = { id?: string; sys_form_section_id: string; sys_column_id: string; column_index?: string | number | null; order: number };

const LEGACY_NEW_KEY = /\.label\.new:/;

export const toEditorSections = (data: { sections: ApiSection[]; elements: ApiElement[] }): EditorSection[] => data.sections.map(row => ({
    // Chaves legadas `...label.new:<uuid>` não são chaves válidas: exibe o texto resolvido; o backend converte ao salvar.
    id: row.id, label: row.label_key && !LEGACY_NEW_KEY.test(row.label_key) ? row.label_key : row.label,
    display: row.label_key ? row.label : undefined,
    layout_columns: String(row.layout_columns) === "2" ? "2" : "1", order: row.order, active: row.active,
    render_mode: row.render_mode === "tab" ? "tab" : "inline",
    show_label: row.show_label !== false,
    elements: data.elements.filter(element => element.sys_form_section_id === row.id).map(element => ({
        id: element.id, sys_column_id: element.sys_column_id, column_index: String(element.column_index) === "2" ? "2" : "1", order: element.order,
    })),
}));

export const toSavePayload = (sections: EditorSection[]) => sections.map(section => ({
    id: section.id.startsWith("new:") ? undefined : section.id, label: section.label, layout_columns: section.layout_columns,
    active: section.active, show_label: section.show_label !== false, render_mode: section.render_mode,
    elements: section.elements.map(element => ({ id: element.id, sys_column_id: element.sys_column_id, column_index: element.column_index })),
}));

/** Nome distinto para seções novas ("Seção 2", "Seção 3"…) até serem renomeadas. */
export function nextSectionName(existing: string[], template: (n: number) => string): string {
    const taken = new Set(existing.map(name => name.trim().toLowerCase()));
    let n = existing.length + 1;
    for (let guard = 0; taken.has(template(n).toLowerCase()) && guard < 1000; guard += 1) n += 1;
    return template(n);
}

/** Sugestão "<Tabela> — <n>" para uma view nova, sem repetir números já usados. */
export function suggestViewName(tableLabel: string, existing: string[]): string {
    const prefix = `${tableLabel} — `;
    const highest = existing.reduce((max, label) => {
        const match = label.startsWith(prefix) ? /^\d+$/.exec(label.slice(prefix.length)) : null;
        return match ? Math.max(max, Number(match[0])) : max;
    }, 0);
    return `${prefix}${highest + 1}`;
}

export const emptySection = (id: string, label: string, layout_columns: "1" | "2" = "1"): EditorSection =>
    ({ id, label, layout_columns, render_mode: "inline", order: 0, active: true, elements: [] });

const normalize = (sections: EditorSection[]): EditorSection[] => sections.map((section, index) => ({
    ...section, order: index + 1,
    elements: section.elements.map((element, position) => ({ ...element, order: position + 1 })),
}));

export function removeField(sections: EditorSection[], columnId: string): EditorSection[] {
    return normalize(sections.map(section => ({ ...section, elements: section.elements.filter(element => element.sys_column_id !== columnId) })));
}

export function moveField(sections: EditorSection[], columnId: string, sectionId: string, columnIndex: "1" | "2", position: number): EditorSection[] {
    const existing = sections.flatMap(section => section.elements).find(element => element.sys_column_id === columnId);
    const without = removeField(sections, columnId);
    return normalize(without.map(section => {
        if (section.id !== sectionId) return section;
        const elements = [...section.elements];
        elements.splice(Math.max(0, Math.min(position, elements.length)), 0,
            { ...existing, sys_column_id: columnId, column_index: section.layout_columns === "1" ? "1" : columnIndex, order: 0 });
        return { ...section, elements };
    }));
}

export function addField(sections: EditorSection[], sectionId: string, columnIndex: "1" | "2", columnId: string): EditorSection[] {
    if (sections.some(section => section.elements.some(element => element.sys_column_id === columnId))) return sections;
    const target = sections.find(section => section.id === sectionId);
    return target ? moveField(sections, columnId, sectionId, columnIndex, target.elements.length) : sections;
}
